import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, isUnauthorized } from "./client";
import type {
    AlertsState,
    AssistantConfigResponse,
    BranchStalenessResponse,
    ClusterDetail,
    Fleet,
    FleetOverview,
    Me,
    PromoteCheckResponse,
    PromotionRequest,
    TimelineResponse,
    TreeEditChange,
    TreeEditRequest,
    TreeResponse,
} from "./types";

const SLOW_MS = 60_000;
const FAST_MS = 15_000;

const ACTIVE_STATES: ReadonlySet<PromotionRequest["state"]> = new Set(["queued", "pipeline_running", "mr_open"]);

export function isActivePromotion(p: PromotionRequest): boolean {
    return ACTIVE_STATES.has(p.state);
}

export const queryKeys = {
    me: ["me"] as const,
    overview: (fleet: Fleet) => ["overview", fleet] as const,
    cluster: (name: string) => ["cluster", name] as const,
    tree: ["tree"] as const,
    alerts: ["alerts"] as const,
    promotions: (state: "active" | "all") => ["promotions", state] as const,
    treeEdits: ["tree-edits"] as const,
    promoteCheck: (envKey: string, toVersion: string | null) => ["promote-check", envKey, toVersion ?? "from-upstream"] as const,
    timeline: (days: number) => ["timeline", days] as const,
    branchStaleness: ["branch-staleness"] as const,
    assistantConfig: ["assistant-config"] as const,
};

export function useMe() {
    return useQuery({
        queryKey: queryKeys.me,
        queryFn: () => apiGet<Me>("/api/me"),
        retry: (failureCount, error) => !isUnauthorized(error) && failureCount < 1,
        staleTime: Infinity,
    });
}

export function useFleetOverview(fleet: Fleet, enabled = true) {
    return useQuery({
        queryKey: queryKeys.overview(fleet),
        queryFn: () => apiGet<FleetOverview>(`/api/fleets/${fleet}/overview`),
        refetchInterval: SLOW_MS,
        enabled,
    });
}

export function useClusterDetail(name: string | null) {
    return useQuery({
        queryKey: queryKeys.cluster(name ?? "-"),
        queryFn: () => apiGet<ClusterDetail>(`/api/clusters/${encodeURIComponent(name ?? "")}`),
        enabled: name !== null,
        refetchInterval: SLOW_MS,
    });
}

export function useTree(enabled = true) {
    return useQuery({
        queryKey: queryKeys.tree,
        queryFn: () => apiGet<TreeResponse>("/api/tree"),
        enabled,
        refetchInterval: SLOW_MS,
    });
}

export function useTimeline(days: number, enabled = true) {
    return useQuery({
        queryKey: queryKeys.timeline(days),
        queryFn: () => apiGet<TimelineResponse>(`/api/timeline?days=${days}`),
        enabled,
        refetchInterval: SLOW_MS,
    });
}

export function useBranchStaleness(enabled = true) {
    return useQuery({
        queryKey: queryKeys.branchStaleness,
        queryFn: () => apiGet<BranchStalenessResponse>("/api/branch-staleness"),
        enabled,
        refetchInterval: SLOW_MS,
    });
}

export function useAssistantConfig(enabled = true) {
    return useQuery({
        queryKey: queryKeys.assistantConfig,
        queryFn: () => apiGet<AssistantConfigResponse>("/api/assistant-config"),
        enabled,
        refetchInterval: SLOW_MS,
    });
}

export function useAlerts(enabled = true) {
    return useQuery({
        queryKey: queryKeys.alerts,
        queryFn: () => apiGet<AlertsState>("/api/alerts"),
        enabled,
        refetchInterval: SLOW_MS,
    });
}

export function usePromotions(state: "active" | "all", enabled = true) {
    return useQuery({
        queryKey: queryKeys.promotions(state),
        queryFn: () => apiGet<PromotionRequest[]>(`/api/promotions?state=${state}`),
        enabled,
        // Tighten polling while something is in flight so MR/pipeline state stays fresh.
        refetchInterval: (query) => (query.state.data?.some(isActivePromotion) ? FAST_MS : SLOW_MS),
    });
}

export function useTreeEdits(enabled = true) {
    return useQuery({
        queryKey: queryKeys.treeEdits,
        queryFn: () => apiGet<TreeEditRequest[]>("/api/tree-edits"),
        enabled,
        refetchInterval: SLOW_MS,
    });
}

export function usePromoteCheck(envKey: string, toVersion: string | null) {
    return useQuery({
        queryKey: queryKeys.promoteCheck(envKey, toVersion),
        queryFn: () => {
            const params = new URLSearchParams({ envKey });
            if (toVersion !== null) params.set("toVersion", toVersion);
            return apiGet<PromoteCheckResponse>(`/api/promote-check?${params.toString()}`);
        },
        staleTime: 0,
    });
}

export function usePromoteMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: { envKey: string; toVersion?: string }) => apiPost<PromotionRequest>("/api/promote", input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["promotions"] });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tree });
            void queryClient.invalidateQueries({ queryKey: queryKeys.overview("argots") });
        },
    });
}

export function useTreeEditMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: { changes: TreeEditChange[]; summary: string }) => apiPost<TreeEditRequest>("/api/tree-edits", input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.treeEdits });
            void queryClient.invalidateQueries({ queryKey: queryKeys.tree });
        },
    });
}

export function useRefreshMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (scope: string) => apiPost<unknown>("/api/refresh", { scope }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["overview"] });
            void queryClient.invalidateQueries({ queryKey: ["cluster"] });
        },
    });
}
